/**
 * Integrytas Group - Base App (MOBILE)
 * Shared approvals-inbox data hook.
 *
 * Single source of truth for "what is awaiting MY approval", consumed by BOTH:
 *   - MobileMyApprovalsPage (APR.INBOX) — renders the list, and
 *   - MobileShell — badges the Approvals bottom-tab with the same count.
 *
 * Because the count and the list come from the same query, the tab badge can
 * never disagree with the inbox it opens (defect: "badge counts differently
 * than the list"). Read-only over apiDb (RLS applies via the gateway JWT); the
 * write path stays the APR engine RPCs invoked by the page, never here.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';

/** A pending approval step assigned to me (decision still null). */
export interface StepRow {
  approval_step_id: number;
  approval_request_id: number;
  level: number;
  approver_source: string | null;
  decision: string | null;
}

/** The request the step belongs to (only PENDING requests are actionable). */
export interface RequestRow {
  approval_request_id: number;
  entity_name: string;
  record_id: string | null;
  state: string;
  current_level: number;
  active_levels: number[] | null;
  staged_operation: string | null;
  requested_by_user_id: number | null;
  requested_at: string;
}

/** A row of the inbox: an actionable step paired with its request + resolved requester. */
export interface InboxItem {
  step: StepRow;
  request: RequestRow;
  requesterName: string;
}

export interface ApprovalInbox {
  items: InboxItem[];
  count: number;
  busy: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Load the caller's actionable approval inbox. A step is actionable only when
 * its level is in the request's active set (parallel stages activate several
 * levels at once; falls back to current_level).
 */
export function useApprovalInbox(session: AuthSession): ApprovalInbox {
  const myId = session.effective_user.user_id;
  const [items, setItems] = useState<InboxItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // My still-open steps.
      const { data: rawSteps, error: sErr } = await apiDb
        .from('apr_step')
        .select('approval_step_id, approval_request_id, level, approver_source, decision')
        .eq('approver_user_id', myId)
        .is('decision', null);
      if (sErr) throw sErr;

      const steps = (rawSteps ?? []) as StepRow[];
      const reqIds = [...new Set(steps.map((s) => s.approval_request_id))];
      if (reqIds.length === 0) {
        setItems([]);
        return;
      }

      // The pending requests those steps belong to.
      const { data: rawReqs, error: rErr } = await apiDb
        .from('apr_request')
        .select('approval_request_id, entity_name, record_id, state, current_level, active_levels, staged_operation, requested_by_user_id, requested_at')
        .in('approval_request_id', reqIds)
        .eq('state', 'PENDING');
      if (rErr) throw rErr;

      const requests = (rawReqs ?? []) as RequestRow[];
      const reqById = new Map<number, RequestRow>(requests.map((r) => [r.approval_request_id, r] as const));

      // Resolve requester ids to names up front (§17.B4 — never render a raw id).
      const requesterIds = [
        ...new Set(requests.map((r) => r.requested_by_user_id).filter((x): x is number => x != null)),
      ];
      const nameById: Record<number, string> = {};
      if (requesterIds.length) {
        const { data: users, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', requesterIds);
        if (uErr) throw uErr;
        for (const u of (users ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          nameById[u.user_id] = u.display_name || u.email || '—';
        }
      }

      const list: InboxItem[] = [];
      for (const step of steps) {
        const request = reqById.get(step.approval_request_id);
        if (!request) continue;
        const active = request.active_levels ?? [request.current_level];
        if (!active.includes(step.level)) continue;
        list.push({
          step,
          request,
          requesterName:
            request.requested_by_user_id == null ? '—' : (nameById[request.requested_by_user_id] ?? '—'),
        });
      }
      setItems(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [myId]);

  useEffect(() => { void reload(); }, [reload]);

  return { items, count: items.length, busy, error, reload };
}
